import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShoppingCart, Radar, ArrowDown, Calculator } from "lucide-react";

const LINES = [
  { text: "MONITORAMOS.", cls: "text-slate-50" },
  { text: "RASTREAMOS.", cls: "text-outline" },
  { text: "RESGATAMOS.", cls: "text-[#A3E635]" },
];

const BLIPS = [
  { top: "22%", left: "64%", delay: "0s" },
  { top: "58%", left: "30%", delay: "0.8s" },
  { top: "70%", left: "72%", delay: "1.5s" },
  { top: "36%", left: "42%", delay: "2.1s" },
];

const EASE = [0.16, 1, 0.3, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yRadar = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="topo"
      ref={ref}
      className="bg-grid relative flex min-h-screen items-center overflow-hidden pt-16"
      data-testid="hero-section"
    >
      <div className="pointer-events-none absolute -top-40 right-[-10%] h-[36rem] w-[36rem] rounded-full bg-[radial-gradient(circle,rgba(255,87,34,0.18)_0%,rgba(30,58,138,0)_70%)]" />
      <div className="pointer-events-none absolute bottom-[-20%] left-[-10%] h-[30rem] w-[30rem] rounded-full bg-[radial-gradient(circle,rgba(6,182,212,0.12)_0%,rgba(30,58,138,0)_70%)]" />

      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-5 py-20 lg:grid-cols-2 lg:px-8">
        <motion.div style={{ y: yText, opacity }}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.35 }}
            className="mb-8 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2"
            data-testid="hero-eyebrow"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#10B981] opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#10B981]" />
            </span>
            <span className="font-tech text-[11px] uppercase tracking-[0.25em] text-slate-300">
              Gestão de prevenção de perdas · Brasil
            </span>
          </motion.div>

          <h1 className="font-display text-5xl font-black leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl" data-testid="hero-headline">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, ease: EASE, delay: 0.45 + i * 0.16 }}
                  className={`block ${line.cls}`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 1.05 }}
            className="mt-7 max-w-lg text-base leading-relaxed text-slate-300 sm:text-lg"
            data-testid="hero-subtitle"
          >
            Transformamos o extravio de carrinhos de supermercado em ativos — com atuação
            nacional, conformidade legal e mais de{" "}
            <strong className="text-[#A3E635]">50% de redução</strong> no investimento em
            novos carrinhos.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 1.2 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#economia"
              data-testid="hero-cta-simulate"
              className="group inline-flex items-center gap-2 rounded-full bg-[#FF5722] px-7 py-4 font-tech text-xs font-bold uppercase tracking-[0.15em] text-[#1E3A8A] transition-all duration-200 hover:-translate-y-1 hover:bg-[#E64A19] hover:shadow-[0_12px_40px_rgba(255,87,34,0.45)]"
            >
              <Calculator className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
              Simular economia
            </a>
            <a
              href="#contato"
              data-testid="hero-cta-contact"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-4 font-tech text-xs font-bold uppercase tracking-[0.15em] text-slate-100 transition-all duration-200 hover:-translate-y-1 hover:border-[#06B6D4] hover:text-[#06B6D4]"
            >
              Falar com especialista
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          style={{ y: yRadar }}
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, ease: EASE, delay: 0.7 }}
          className="relative mx-auto w-full max-w-[460px]"
          data-testid="hero-radar-panel"
        >
          <div className="relative aspect-square overflow-hidden rounded-2xl border border-white/10 bg-[#1B3579]/80 shadow-[0_30px_80px_rgba(0,0,0,0.5)] backdrop-blur">
            <div className="absolute inset-0 p-6">
              <svg viewBox="0 0 100 100" className="h-full w-full opacity-60">
                {[46, 34, 22, 10].map((r) => (
                  <circle key={r} cx="50" cy="50" r={r} fill="none" stroke="rgba(148,163,184,0.25)" strokeWidth="0.3" />
                ))}
                <line x1="50" y1="4" x2="50" y2="96" stroke="rgba(148,163,184,0.2)" strokeWidth="0.3" />
                <line x1="4" y1="50" x2="96" y2="50" stroke="rgba(148,163,184,0.2)" strokeWidth="0.3" />
              </svg>
            </div>
            <div className="radar-sweep absolute inset-6 rounded-full" />
            {BLIPS.map((b, i) => (
              <div key={i} className="absolute flex items-center gap-1.5" style={{ top: b.top, left: b.left }}>
                <div className="relative">
                  <span className="blip-ping absolute -inset-2 rounded-full bg-[#FF5722]/50" style={{ animationDelay: b.delay }} />
                  <span className="relative block h-2.5 w-2.5 rounded-full bg-[#FF5722] shadow-[0_0_12px_rgba(255,87,34,0.9)]" />
                </div>
                <ShoppingCart className="h-3.5 w-3.5 text-[#06B6D4] drop-shadow-[0_0_6px_rgba(6,182,212,0.8)]" />
              </div>
            ))}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-[#06B6D4]/40 bg-white p-1 shadow-[0_0_30px_rgba(6,182,212,0.35)]">
                <img src="/assets/logo-nova-symbol.png" alt="ResgateCar" className="h-full w-full object-contain" />
              </div>
            </div>
            <div className="absolute left-4 top-4 flex items-center gap-2 font-tech text-[10px] uppercase tracking-[0.2em] text-slate-400">
              <Radar className="h-3.5 w-3.5 text-[#A3E635]" />
              Varredura ativa
            </div>
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between font-tech text-[10px] uppercase tracking-[0.18em] text-slate-400" data-testid="hero-telemetry">
              <span>
                Alvos localizados: <span className="text-[#10B981]">04</span>
              </span>
              <span>
                Perímetro: <span className="text-[#06B6D4]">3.2 km</span>
              </span>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 1.4 }}
            className="absolute -right-3 -top-5 rounded-xl border border-white/10 bg-[#1E3A8A]/95 px-4 py-3 shadow-xl sm:-right-6"
            data-testid="hero-floating-stat"
          >
            <p className="font-display text-2xl font-extrabold text-[#A3E635]">mais de 50%</p>
            <p className="font-tech text-[10px] uppercase tracking-[0.15em] text-slate-400">
              menos investimento em reposição
            </p>
          </motion.div>
        </motion.div>
      </div>

      <motion.a
        href="#impacto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-slate-500 transition-colors hover:text-[#A3E635] lg:flex"
        data-testid="hero-scroll-indicator"
        aria-label="Rolar para baixo"
      >
        <span className="font-tech text-[10px] uppercase tracking-[0.3em]">Explore</span>
        <ArrowDown className="h-4 w-4 animate-bounce" />
      </motion.a>
    </section>
  );
};

export default Hero;
